import { formatMoney, formatDateTime } from '@/lib/format';
import type { Branch, Sale, SaleItem } from '@/lib/types';
import { paymentLabel, saleCashPortion, saleCardPortion } from '@/lib/types';
import type { PaperWidth } from './printTypes';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type PrintFormat = '58mm' | '80mm' | 'A4';

/** Normaliza el ancho de papel. Cualquier valor desconocido cae a 80mm. */
function resolveFormat(paperWidth?: PaperWidth | string): PrintFormat {
  if (paperWidth === '58mm') return '58mm';
  if (paperWidth === 'A4') return 'A4';
  return '80mm';
}

/**
 * Devuelve el ancho imprimible REAL de cada rollo térmico.
 *
 * El papel de 80mm solo tiene ~72mm imprimibles (≈4mm no imprimibles por lado)
 * y el de 58mm solo ~48mm. Si usamos el ancho completo del papel, todo lo que
 * va a la derecha (precios, subtotales, totales) cae fuera del cabezal y se
 * corta. Por eso fijamos el área de contenido al ancho imprimible y dejamos el
 * margen físico en el padding.
 */
const RECEIPT_CFG: Record<'58mm' | '80mm', {
  page: string;
  content: string;
  offset: string;
  font: string;
  small: string;
  logoMax: string;
  logoH: string;
}> = {
  // El contenido se ancla a la IZQUIERDA (no se centra) y su ancho es MENOR que
  // el del papel a propósito: así cubre la zona no imprimible del cabezal
  // térmico y la columna derecha (precios, subtotales, totales) NUNCA queda
  // fuera del área imprimible ni se corta.
  '80mm': { page: '80mm', content: '68mm', offset: '2mm', font: '13px', small: '11px', logoMax: '50mm', logoH: '18mm' },
  '58mm': { page: '58mm', content: '44mm', offset: '2mm', font: '11px', small: '10px', logoMax: '38mm', logoH: '15mm' },
};

function receiptCss(format: '58mm' | '80mm'): string {
  const cfg = RECEIPT_CFG[format];
  return `
  @page { size: ${cfg.page} auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; width: ${cfg.page}; background: #fff; }
  body {
    font-family: 'Courier New', Courier, monospace;
    font-weight: 700;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    text-rendering: geometricPrecision;
  }
  /* El contenido se ANCLA A LA IZQUIERDA del papel (no se centra): así se
     aprovecha el espacio libre de la izquierda y la columna derecha entra
     completa dentro del área imprimible del cabezal térmico. */
  .receipt { width: ${cfg.content}; margin: 0 0 0 ${cfg.offset}; padding: 2mm 0 3mm; font-size: ${cfg.font}; line-height: 1.5; }
  .center { text-align: center; }
  .bold { font-weight: 900; }
  .logo { display: block; margin: 0 auto 2mm; max-width: ${cfg.logoMax}; max-height: ${cfg.logoH}; object-fit: contain; }
  /* Fila de 2 columnas con GRID: izquierda flexible (hace wrap) y derecha al
     tamaño exacto del contenido. Grid evita que el valor derecho se desborde. */
  .row { display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 3px; align-items: start; margin: 0.6mm 0; }
  .row > .l { min-width: 0; overflow-wrap: anywhere; word-break: break-word; }
  .row > .r { white-space: nowrap; text-align: right; }
  .item { margin: 0.8mm 0; }
  .item-name { font-weight: 700; overflow-wrap: anywhere; word-break: break-word; }
  .divider { border-top: 1px dashed #000; margin: 2.4mm 0; }
  .small { font-size: ${cfg.small}; }
  .nowrap { white-space: nowrap; }
`;
}

/**
 * CSS del formato A4.
 *
 * El tamaño y el margen se fijan SOLO con @page (margen 14mm). No añadimos
 * padding extra al contenedor para no duplicar márgenes ni reducir de más el
 * área imprimible. Las tablas usan `table-layout: fixed` con anchos por
 * columna para que los números de la derecha nunca se desborden.
 */
const A4_CSS = `
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.5;
    text-rendering: geometricPrecision;
  }
  .page { width: 100%; }
  .center { text-align: center; }
  .bold { font-weight: 800; }
  .header { border-bottom: 2px solid #222; padding-bottom: 10px; }
  .meta { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 4px 20px; margin: 12px 0; }
  .meta .lbl { color: #555; }
  table.items { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.items th, table.items td { border-bottom: 1px solid #ddd; padding: 6px; text-align: left; vertical-align: top; }
  table.items th { font-weight: 700; }
  table.items td:first-child { font-weight: 600; }
  table.items th.num, table.items td.num { text-align: right; white-space: nowrap; }
  table.items thead { display: table-header-group; }
  table.items tr { page-break-inside: avoid; }
  .muted { color: #555; }
  .totals { width: 280px; margin-left: auto; margin-top: 12px; border-collapse: collapse; table-layout: fixed; }
  .totals td { padding: 3px 0; }
  .totals td.amt { text-align: right; white-space: nowrap; }
  .footer { text-align: center; margin-top: 22px; color: #555; }
  .badge { display: inline-block; border: 1px solid #999; border-radius: 3px; padding: 0 4px; font-size: 10px; font-weight: 700; }
`;

export interface InvoicePrintData {
  branch: Branch | null;
  sale: Sale;
  items: SaleItem[];
  currencySymbol?: string;
  showCashier?: boolean;
  showPayment?: boolean;
  showLogo?: boolean;
  showAddress?: boolean;
  showPhone?: boolean;
  showRnc?: boolean;
  showDatetime?: boolean;
  footerMessage?: string;
}

export interface TestPrintParams {
  printerName?: string | null;
  paperWidth?: PaperWidth | string;
  runtime: string;
}

export function buildTestPrintHTML(params: TestPrintParams): string {
  const format = resolveFormat(params.paperWidth);
  const now = new Date();
  const date = now.toLocaleDateString('es-DO');
  const time = now.toLocaleTimeString('es-DO');
  const css = format === 'A4' ? A4_CSS : receiptCss(format);
  const body =
    format === 'A4'
      ? `<div class="page">
          <div class="center bold" style="font-size:18px;">SISTEMA DE FACTURACIÓN</div>
          <div class="center">PRUEBA DE IMPRESIÓN</div>
          <div class="meta">
            <div><span class="lbl">Impresora:</span> ${escapeHtml(params.printerName || 'Navegador')}</div>
            <div><span class="lbl">Formato:</span> ${escapeHtml(String(params.paperWidth || '—'))}</div>
            <div><span class="lbl">Entorno:</span> ${escapeHtml(params.runtime)}</div>
            <div><span class="lbl">Fecha:</span> ${date} ${time}</div>
          </div>
          <div class="center bold">PRUEBA CORRECTA</div>
        </div>`
      : `<div class="receipt">
          <div class="center bold">================================</div>
          <div class="center bold">SISTEMA DE FACTURACIÓN</div>
          <div class="center bold">================================</div>
          <div class="divider"></div>
          <div class="center bold">PRUEBA DE IMPRESIÓN</div>
          <div class="divider"></div>
          <div class="row"><span class="l">Impresora:</span><span class="r">${escapeHtml(params.printerName || 'Navegador')}</span></div>
          <div class="row"><span class="l">Formato:</span><span class="r">${escapeHtml(String(params.paperWidth || '—'))}</span></div>
          <div class="row"><span class="l">Entorno:</span><span class="r">${escapeHtml(params.runtime)}</span></div>
          <div class="row"><span class="l">Fecha:</span><span class="r">${date}</span></div>
          <div class="row"><span class="l">Hora:</span><span class="r">${time}</span></div>
          <div class="divider"></div>
          <div class="center bold">PRUEBA CORRECTA</div>
          <div class="center bold">================================</div>
        </div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><style>${css}</style></head><body>${body}</body></html>`;
}

interface ItemLine {
  name: string;
  qty: number;
  unitPrice: number;
  subtotal: number;
  discount: number;
  discountPct: number | null;
  isWholesale: boolean;
  authorizedBy: string | null;
}

function toItemLines(items: SaleItem[]): ItemLine[] {
  return items.map((it) => ({
    name: it.product_name,
    qty: Number(it.quantity || 0),
    unitPrice: Number(it.unit_price || 0),
    subtotal: Number(it.subtotal || 0),
    discount: Number(it.discount || 0),
    discountPct: it.discount_percentage != null ? Number(it.discount_percentage) : null,
    isWholesale: !!it.is_wholesale,
    authorizedBy: it.discount_authorized_by_name ?? null,
  }));
}

export function buildReceiptHTML(data: InvoicePrintData, paperWidth?: PaperWidth | string): string {
  const {
    branch,
    sale,
    items,
    currencySymbol = 'RD$',
    showCashier = true,
    showPayment = true,
    showLogo = true,
    showAddress = true,
    showPhone = true,
    showRnc = true,
    showDatetime = true,
  } = data;

  const format = resolveFormat(paperWidth === 'A4' ? '80mm' : paperWidth);
  const footer = data.footerMessage || branch?.description || 'Gracias por su compra.';

  const itemRows = toItemLines(items)
    .map((it) => {
      const discRow =
        it.discount > 0
          ? `<div class="row small"><span class="l">Descuento${
              it.discountPct != null ? ` ${it.discountPct}%` : ''
            }:</span><span class="r">-${formatMoney(it.discount, currencySymbol)}</span></div>`
          : '';
      const wholesaleRow = it.isWholesale
        ? `<div class="row small"><span class="l">Mayorista</span><span class="r">✓</span></div>`
        : '';
      const authRow = it.authorizedBy
        ? `<div class="row small"><span class="l">Autorizado por</span><span class="r">${escapeHtml(it.authorizedBy)}</span></div>`
        : '';
      return `
      <div class="item">
        <div class="item-name">${escapeHtml(it.name)}</div>
        <div class="row small">
          <span class="l">${it.qty} x ${formatMoney(it.unitPrice, currencySymbol)}</span>
          <span class="r">${formatMoney(it.subtotal, currencySymbol)}</span>
        </div>${discRow}${wholesaleRow}${authRow}
      </div>`;
    })
    .join('');

  const codeDiscount = Number(sale.discount_code_amount ?? 0);
  const lineDiscount = Math.max(0, Number(sale.discount ?? 0) - codeDiscount);

  return `<!doctype html><html><head><meta charset="utf-8"><title>Factura ${escapeHtml(
    sale.invoice_number,
  )}</title><style>${receiptCss(format)}</style></head><body>
    <div class="receipt">
      ${showLogo && branch?.logo_url ? `<img class="logo" src="${escapeHtml(branch.logo_url)}" alt="Logo" />` : ''}
      <div class="center bold">${escapeHtml(branch?.name || 'Factura')}</div>
      ${branch?.legal_name ? `<div class="center small">${escapeHtml(branch.legal_name)}</div>` : ''}
      ${showAddress && branch?.address ? `<div class="center small">${escapeHtml(branch.address)}</div>` : ''}
      ${showPhone && branch?.phone ? `<div class="center small">${escapeHtml(branch.phone)}</div>` : ''}
      ${showRnc && branch?.rnc ? `<div class="center small">RNC: ${escapeHtml(branch.rnc)}</div>` : ''}
      <div class="divider"></div>
      <div class="row"><span class="l bold">Factura:</span><span class="r">${escapeHtml(sale.invoice_number)}</span></div>
      ${showDatetime ? `<div class="row"><span class="l">Fecha:</span><span class="r">${formatDateTime(sale.created_at)}</span></div>` : ''}
      ${showCashier && sale.cashier_name ? `<div class="row"><span class="l">Cajero:</span><span class="r">${escapeHtml(sale.cashier_name)}</span></div>` : ''}
      <div class="row"><span class="l">Cliente:</span><span class="r">${escapeHtml(sale.customer_name || 'Cliente general')}</span></div>
      <div class="divider"></div>
      ${itemRows}
      <div class="divider"></div>
      <div class="row"><span class="l">Subtotal:</span><span class="r">${formatMoney(Number(sale.subtotal), currencySymbol)}</span></div>
      ${lineDiscount > 0 ? `<div class="row"><span class="l">Descuentos:</span><span class="r">-${formatMoney(lineDiscount, currencySymbol)}</span></div>` : ''}
      ${codeDiscount > 0 ? `<div class="row"><span class="l">Desc. código${sale.discount_code ? ` (${escapeHtml(sale.discount_code)})` : ''}:</span><span class="r">-${formatMoney(codeDiscount, currencySymbol)}</span></div>` : ''}
      ${Number(sale.tax) > 0 ? `<div class="row"><span class="l">Impuesto:</span><span class="r">${formatMoney(Number(sale.tax), currencySymbol)}</span></div>` : ''}
      <div class="row bold"><span class="l">TOTAL:</span><span class="r">${formatMoney(Number(sale.total), currencySymbol)}</span></div>
      ${showPayment ? `<div class="row"><span class="l">Pago:</span><span class="r">${escapeHtml(paymentLabel(sale.payment_method))}</span></div>` : ''}
      ${sale.payment_method === 'cash' && Number(sale.amount_received) > 0 ? `<div class="row"><span class="l">Recibido:</span><span class="r">${formatMoney(Number(sale.amount_received), currencySymbol)}</span></div>` : ''}
      ${sale.payment_method === 'cash' && Number(sale.change_amount) > 0 ? `<div class="row"><span class="l">Cambio:</span><span class="r">${formatMoney(Number(sale.change_amount), currencySymbol)}</span></div>` : ''}
      ${showPayment && sale.payment_method === 'mixed' ? `<div class="row"><span class="l">Efectivo:</span><span class="r">${formatMoney(saleCashPortion(sale), currencySymbol)}</span></div>` : ''}
      ${showPayment && sale.payment_method === 'mixed' ? `<div class="row"><span class="l">Tarjeta:</span><span class="r">${formatMoney(saleCardPortion(sale), currencySymbol)}</span></div>` : ''}
      <div class="divider"></div>
      <div class="center small">${escapeHtml(footer)}</div>
    </div>
  </body></html>`;
}

export function buildA4InvoiceHTML(data: InvoicePrintData): string {
  const {
    branch,
    sale,
    items,
    currencySymbol = 'RD$',
    showCashier = true,
    showPayment = true,
    showLogo = true,
    showAddress = true,
    showPhone = true,
    showRnc = true,
    showDatetime = true,
  } = data;

  const footer = data.footerMessage || branch?.description || 'Gracias por su compra.';

  const itemRows = toItemLines(items)
    .map((it) => {
      const discLine =
        it.discount > 0
          ? `<div class="muted" style="font-size:11px;">Descuento${
              it.discountPct != null ? ` ${it.discountPct}%` : ''
            }: -${formatMoney(it.discount, currencySymbol)}</div>`
          : '';
      const wholesaleBadge = it.isWholesale ? ` <span class="badge">MAYORISTA</span>` : '';
      const authLine = it.authorizedBy
        ? `<div class="muted" style="font-size:11px;">Autorizado por ${escapeHtml(it.authorizedBy)}</div>`
        : '';
      return `
      <tr>
        <td>${escapeHtml(it.name)}${wholesaleBadge}${discLine}${authLine}</td>
        <td class="num">${it.qty}</td>
        <td class="num">${formatMoney(it.unitPrice, currencySymbol)}</td>
        <td class="num">${formatMoney(it.subtotal, currencySymbol)}</td>
      </tr>`;
    })
    .join('');

  const codeDiscount = Number(sale.discount_code_amount ?? 0);
  const lineDiscount = Math.max(0, Number(sale.discount ?? 0) - codeDiscount);

  return `<!doctype html><html><head><meta charset="utf-8"><title>Factura ${escapeHtml(
    sale.invoice_number,
  )}</title><style>${A4_CSS}</style></head><body>
    <div class="page">
      <div class="center header">
        ${showLogo && branch?.logo_url ? `<img src="${escapeHtml(branch.logo_url)}" alt="Logo" style="height:56px;margin-bottom:8px;" />` : ''}
        <div class="bold" style="font-size:18px;">${escapeHtml(branch?.name || 'Factura')}</div>
        ${branch?.legal_name ? `<div>${escapeHtml(branch.legal_name)}</div>` : ''}
        ${showAddress && branch?.address ? `<div class="muted">${escapeHtml(branch.address)}</div>` : ''}
        ${showPhone && branch?.phone ? `<div class="muted">${escapeHtml(branch.phone)}</div>` : ''}
        ${showRnc && branch?.rnc ? `<div class="muted">RNC: ${escapeHtml(branch.rnc)}</div>` : ''}
      </div>
      <div class="meta">
        <div><span class="lbl">Factura:</span> <b>${escapeHtml(sale.invoice_number)}</b></div>
        ${showDatetime ? `<div><span class="lbl">Fecha:</span> ${formatDateTime(sale.created_at)}</div>` : ''}
        ${showCashier && sale.cashier_name ? `<div><span class="lbl">Cajero:</span> ${escapeHtml(sale.cashier_name)}</div>` : ''}
        <div><span class="lbl">Cliente:</span> ${escapeHtml(sale.customer_name || 'Cliente general')}</div>
      </div>
      <table class="items">
        <colgroup>
          <col />
          <col style="width:52px;" />
          <col style="width:100px;" />
          <col style="width:110px;" />
        </colgroup>
        <thead>
          <tr>
            <th>Producto</th>
            <th class="num">Cant.</th>
            <th class="num">Precio</th>
            <th class="num">Subtotal</th>
          </tr>
        </thead>
        <tbody>${itemRows}</tbody>
      </table>
      <table class="totals">
        <tr><td class="muted">Subtotal</td><td class="amt">${formatMoney(Number(sale.subtotal), currencySymbol)}</td></tr>
        ${lineDiscount > 0 ? `<tr><td class="muted">Descuentos</td><td class="amt">-${formatMoney(lineDiscount, currencySymbol)}</td></tr>` : ''}
        ${codeDiscount > 0 ? `<tr><td class="muted">Desc. código${sale.discount_code ? ` (${escapeHtml(sale.discount_code)})` : ''}</td><td class="amt">-${formatMoney(codeDiscount, currencySymbol)}</td></tr>` : ''}
        ${Number(sale.tax) > 0 ? `<tr><td class="muted">Impuesto</td><td class="amt">${formatMoney(Number(sale.tax), currencySymbol)}</td></tr>` : ''}
        <tr><td class="bold">Total</td><td class="bold amt">${formatMoney(Number(sale.total), currencySymbol)}</td></tr>
        ${showPayment ? `<tr><td class="muted">Pago</td><td class="amt">${escapeHtml(paymentLabel(sale.payment_method))}</td></tr>` : ''}
        ${sale.payment_method === 'cash' && Number(sale.amount_received) > 0 ? `<tr><td class="muted">Recibido</td><td class="amt">${formatMoney(Number(sale.amount_received), currencySymbol)}</td></tr>` : ''}
        ${sale.payment_method === 'cash' && Number(sale.change_amount) > 0 ? `<tr><td class="muted">Cambio</td><td class="amt">${formatMoney(Number(sale.change_amount), currencySymbol)}</td></tr>` : ''}
        ${showPayment && sale.payment_method === 'mixed' ? `<tr><td class="muted">Efectivo</td><td class="amt">${formatMoney(saleCashPortion(sale), currencySymbol)}</td></tr>` : ''}
        ${showPayment && sale.payment_method === 'mixed' ? `<tr><td class="muted">Tarjeta</td><td class="amt">${formatMoney(saleCardPortion(sale), currencySymbol)}</td></tr>` : ''}
      </table>
      <div class="footer">${escapeHtml(footer)}</div>
    </div>
  </body></html>`;
}

export function buildInvoiceHTML(data: InvoicePrintData, paperWidth?: PaperWidth | string): string {
  return paperWidth === 'A4' ? buildA4InvoiceHTML(data) : buildReceiptHTML(data, paperWidth);
}